"use client";

import { usePathname, useRouter } from "next/navigation";
import {
  LayoutDashboard,
  Users,
  Package,
  ShoppingCart,
  Wallet,
  CreditCard,
  Boxes,
  ChevronDown,
  PackageCheck,
  PlusSquare,
  Ticket,
  ClipboardList,
  Building2,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import Image from "next/image";
import ccLogo from "../assets/icons/Cycle-Circle-Logo-Orange-1.png";
import { useEffect, useRef, useState, JSX } from "react";
import gsap from "gsap";

const MobileSidebar = ({
  isSidebarOpen,
  toggleSidebar,
}: {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
}) => {
  const pathname = usePathname();
  const [isInventoryOpen, setIsInventoryOpen] = useState(false);
  const navRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();

  useEffect(() => {
    if (navRef.current) {
      gsap.fromTo(
        navRef.current.children,
        { x: -50, opacity: 0 },
        { x: 0, opacity: 1, stagger: 0.1, ease: "power2.out", duration: 0.6 }
      );
    }
  }, []);

  // Prefetch B2B routes when sidebar opens
  useEffect(() => {
    if (isSidebarOpen) {
      router.prefetch("/select-business");
      router.prefetch("/b2b/dashboard");
      router.prefetch("/b2b/users");
      router.prefetch("/b2b/orders");
      router.prefetch("/b2b/user-cart");
      router.prefetch("/b2b/products");
      router.prefetch("/b2b/brands");
      router.prefetch("/b2b/manage-inventory");
      router.prefetch("/b2b/coupons");
      router.prefetch("/b2b/wallets");
      router.prefetch("/b2b/line-of-credit");
    }
  }, [isSidebarOpen, router]);

  return (
    <div>
      {/* Sidebar */}
      <Card
        className={cn(
          "fixed left-0 top-0 h-screen w-64 bg-gradient-to-b rounded-none from-gray-900 to-gray-800 text-white shadow-xl p-4 z-50 transition-transform duration-300",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <Image
            src={ccLogo}
            priority
            alt="cc-logo"
            width={100}
            height={50}
            className="object-contain"
          />
        </div>

        {/* Navigation Links */}
        <nav ref={navRef} className="space-y-2">
          <div className="pb-2 border-b border-gray-600 mb-5">
            <SidebarLink
              href="/select-business"
              icon={<Building2 size={20} />}
              text="Switch Business"
              pathname={pathname}
              toggleSidebar={toggleSidebar}
            />
          </div>
          <SidebarLink
            href="/b2b/dashboard"
            icon={<LayoutDashboard size={20} />}
            text="Dashboard"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2b/users"
            icon={<Users size={20} />}
            text="Users"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2b/orders"
            icon={<ClipboardList size={20} />}
            text="Orders"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2b/user-cart"
            icon={<ShoppingCart size={20} />}
            text="User Cart"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2b/products"
            icon={<Package size={20} />}
            text="Products"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />

          {/* Manage Inventory (Collapsible) */}
          <button
            onClick={() => setIsInventoryOpen(!isInventoryOpen)}
            className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition-all"
          >
            <div className="flex items-center space-x-3">
              <Boxes size={20} />
              <span className="font-medium">Manage Inventory</span>
            </div>
            <ChevronDown
              size={18}
              className={`transition-transform ${
                isInventoryOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {isInventoryOpen && (
            <div className="pl-8 space-y-2">
              <SidebarLink
                href="/b2b/brands"
                icon={<PackageCheck size={16} />}
                text="Configure Brand"
                pathname={pathname}
                toggleSidebar={toggleSidebar}
              />
              <SidebarLink
                href="/b2b/manage-inventory"
                icon={<PlusSquare size={16} />}
                text="Manage Inventory"
                pathname={pathname}
                toggleSidebar={toggleSidebar}
              />
            </div>
          )}

          <SidebarLink
            href="/b2b/coupons"
            icon={<Ticket size={20} />}
            text="Add Coupon"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2b/wallets"
            icon={<Wallet size={20} />}
            text="Wallet"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
          <SidebarLink
            href="/b2b/line-of-credit"
            icon={<CreditCard size={20} />}
            text="Line of Credit"
            pathname={pathname}
            toggleSidebar={toggleSidebar}
          />
        </nav>
      </Card>
    </div>
  );
};

const SidebarLink = ({
  href,
  icon,
  text,
  pathname,
  toggleSidebar,
}: {
  href: string;
  icon: JSX.Element;
  text: string;
  pathname: string;
  toggleSidebar: () => void;
}) => {
  const isActive = pathname === href;
  const router = useRouter();

  return (
    <button
      onClick={() => {
        toggleSidebar();
        router.push(href);
      }}
      className={cn(
        "w-full cursor-pointer flex items-center space-x-3 px-4 py-3 rounded-lg transition-all group",
        isActive
          ? "bg-white text-black shadow-md"
          : "text-gray-300 hover:bg-gray-700 hover:text-white"
      )}
    >
      <span
        className={cn(
          "transition-all",
          isActive ? "text-black" : "text-gray-400 group-hover:text-white"
        )}
      >
        {icon}
      </span>
      <span
        className={cn(
          "transition-all font-medium",
          isActive ? "text-black" : "text-gray-300 group-hover:text-white"
        )}
      >
        {text}
      </span>
    </button>
  );
};

export default MobileSidebar;
